/**
 * Shared types and constants for the Budget API (ZPL-223) — capture-first
 * Budget. Single source of truth for the request/response contract that web +
 * android render against. Money is a decimal string (never a float); dates are
 * ISO strings (the house convention).
 *
 * `budget_entries` are FACTS (costs incurred) — distinct from `cost_proposals`
 * (an estimate/quote). The two are never mixed (spec Decyzja 1). v1 is
 * OWNER-ONLY: only the property owner sees or mutates budget.
 */

import { SUPPORTED_PROJECT_CURRENCIES } from "./projects"

/** A draft awaiting owner review (AI capture), a confirmed fact, or a soft reject. */
export const BUDGET_ENTRY_STATUSES = [
	"ai_draft",
	"confirmed",
	"rejected",
] as const
export type BudgetEntryStatus = (typeof BUDGET_ENTRY_STATUSES)[number]

/**
 * Cost category (spec Decyzja 16). `mix` is the explicit "I didn't split
 * material vs labour" bucket; `other` the catch-all. ASCII keys — clients
 * localize the labels (matches the ticket-kind convention).
 */
export const BUDGET_ENTRY_KINDS = [
	"material",
	"labor",
	"service",
	"equipment",
	"mix",
	"other",
] as const
export type BudgetEntryKind = (typeof BUDGET_ENTRY_KINDS)[number]

/** Currencies a budget entry may be denominated in — reuses the project set. */
export const BUDGET_ENTRY_CURRENCIES = SUPPORTED_PROJECT_CURRENCIES
export type BudgetEntryCurrency = (typeof BUDGET_ENTRY_CURRENCIES)[number]

export const BUDGET_ENTRY_LIMITS = {
	descriptionMax: 2000,
	vendorMax: 200,
	/** Decimal string: up to 10 integer digits + 2 fraction digits (numeric(12,2)). */
	amountPattern: /^\d{1,10}(\.\d{1,2})?$/,
} as const

/**
 * A budget entry as returned by the API (JSON-serialized; amounts are decimal
 * strings, dates ISO strings). `amount` is null on a low-confidence / not-yet-
 * extracted AI draft. `converted` is the amount restated in the VIEW CONTEXT
 * currency (property currency for the "whole house" view, project currency for a
 * project-scoped view), computed on the fly at the rate for `occurredAt`; null
 * when no rate is available or amount is null.
 */
export interface BudgetEntryDTO {
	id: string
	propertyId: string
	projectId: string | null
	topicId: string | null
	status: BudgetEntryStatus
	kind: BudgetEntryKind | null
	/** Decimal string in the ORIGINAL currency, or null (low-confidence draft). */
	amount: string | null
	currency: BudgetEntryCurrency
	vendor: string | null
	description: string | null
	occurredAt: string | null
	paidAt: string | null
	/** True when an invoice/proof document is attached (the bytes themselves are
	 *  fetched via a separate signed-URL endpoint — never inlined). */
	hasDocument: boolean
	/** The proof document's id (owner-only; the entry is only ever returned to the
	 *  owner). The OWNER fetches the bytes via the existing
	 *  `GET …/documents/:documentId/download` (the doc is owner-scoped, so a
	 *  non-owner can't download it). Null for a "z palca" entry. */
	documentId: string | null
	createdWithAi: boolean
	createdBy: string
	createdAt: string
	updatedAt: string
	/** Original amount restated in the view-context currency ("≈ przeliczone"). */
	converted?: { amount: string; currency: BudgetEntryCurrency } | null
}

/** Request body for `POST /properties/:propertyId/budget-entries` (manual entry → confirmed). */
export interface CreateBudgetEntryBody {
	/** Decimal string; required for a manual entry. */
	amount: string
	currency?: BudgetEntryCurrency
	kind?: BudgetEntryKind | null
	vendor?: string | null
	description?: string | null
	/** Null/absent = property-level; set = project-scoped (must belong to the property). */
	projectId?: string | null
	topicId?: string | null
	/** ISO date-time; absent → server uses now(). */
	occurredAt?: string | null
}

/**
 * Request body for `PATCH /properties/:propertyId/budget-entries/:id`.
 * At least one field required. `status` has its own endpoint. Empty string on
 * description/vendor clears it (stored null).
 */
export interface UpdateBudgetEntryBody {
	amount?: string
	currency?: BudgetEntryCurrency
	kind?: BudgetEntryKind | null
	vendor?: string | null
	description?: string | null
	projectId?: string | null
	topicId?: string | null
	occurredAt?: string | null
	/** Passive "paid" marker (spec Decyzja 2). ISO date-time, or null to clear. */
	paidAt?: string | null
}

/** Request body for `POST /properties/:propertyId/budget-entries/:id/status`. */
export interface SetBudgetEntryStatusBody {
	status: BudgetEntryStatus
}

/** Request body for `POST /properties/:propertyId/budget-entries/capture` (Flow B). */
export interface CaptureInvoiceBody {
	contentType: string
	filename: string
	declaredSize: number
	projectId?: string | null
	topicId?: string | null
}

export interface BudgetEntryResponse {
	entry: BudgetEntryDTO
}

/**
 * `GET /properties/:propertyId/budget-entries?projectId=&status=&kind=&topicId=&cursor=&limit=`
 * — newest-first, cursor-paginated. `summary` carries the header stats in the
 * view-context currency: `spentConfirmed` = Σ confirmed entries converted to
 * `contextCurrency`; `draftCount` powers the thin "X to review" counter.
 */
export interface BudgetEntryListResponse {
	entries: BudgetEntryDTO[]
	nextCursor: string | null
	summary: {
		contextCurrency: BudgetEntryCurrency
		spentConfirmed: string
		/** Count of confirmed entries EXCLUDED from `spentConfirmed` because no FX
		 *  rate was available for their currency/date. > 0 means the total is
		 *  PARTIAL — the client must flag it ("≈ X, x pozycji bez kursu"); the sum
		 *  is never silently under-reported. */
		excludedFromSum: number
		draftCount: number
	}
}

/**
 * Response to `POST .../budget-entries/capture`. The placeholder `ai_draft`
 * entry already exists (the entry IS the capture marker — no orphan window);
 * the client PUTs the bytes to `upload` then calls
 * `POST .../budget-entries/:id/confirm-capture` to enqueue extraction.
 */
export interface BudgetCaptureResponse {
	entry: BudgetEntryDTO
	upload: { uploadUrl: string; method: string; headers: Record<string, string> }
	expiresAt: string
}

/**
 * Response to `POST .../budget-entries/:id/confirm-capture`. `possibleDuplicate`
 * is the SOFT dedup warning (spec Decyzja 7): another ready document in this
 * property shares this one's content hash. Non-blocking — the owner may proceed.
 */
export interface ConfirmCaptureResponse {
	entry: BudgetEntryDTO
	possibleDuplicate: boolean
}
